import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { useHistory } from 'react-router';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';

import Checkbox from 'components/ui/Checkbox';
import Select from 'components/ui/Select';
import currencies from 'constants/currencies';

import { INBOUND, OUTBOUND, BOTH } from 'constants/booking';

import {
  StyledChevron,
  StyledAssistanceGroup,
  StyledAssistanceGroupHeader,
  StyledAssistanceToggle,
  StyledItems,
  StyledItem,
  StyledItemColumns,
  StyledItemsContainer,
  StyledRemoveButton,
  StyledAddAnotherPassenger,
  StyledStrong,
  StyledItemColumn,
  StyledRadio,
  StyledTextarea,
  StyledButtonWrapper,
  StyledRadioGroup,
  StyledWarningMessage,
  StyledCommentWarning,
  StyledNumberInput
} from './AddAssistanceOptions.styles';
import { hasDog } from 'utils/booking';
import getPassengerLabel from 'utils/getPassengerLabel';
import {
  ASSISTANCE_ANIMAL_OPTIONS,
  ASSISTANCE_KEYS,
  PASSENGER_DISABILITY_KEYS,
  PASSENGER_DISABILITY_OPTIONS
} from '../../../../../constants/assistance';

const getAssistanceDirection = (item) => {
  if (item.numberOfOutboundItems > 0 && item.numberOfInboundItems > 0) {
    return BOTH;
  }

  return item.numberOfOutboundItems > 0 ? OUTBOUND : INBOUND;
};

const AddAssistanceOptions = ({
  intl,
  currency,
  assistance,
  isExtra,
  isGroupBooking,
  directions,
  passengers,
  isAddPassengersDisabled,
  isIncomplete,
  isItemsVisible,
  items,
  onAddItem,
  onChangeItem,
  onRemoveAllItems,
  onRemoveItem,
  onToggleItemsVisibility
}) => {
  const history = useHistory();
  const hasItems = !isEmpty(items);
  const paxIdHeterogenous = new URLSearchParams(history.location?.search).get('passengerId');
  const hasDogPassenger = hasDog({ passengers });

  const isOptionDisabled = (id) => items.find((item) => item.passengerId === id) || !assistance.passengers.includes(id);

  const handleAssistanceSelection = () => {
    const isTryingToRemoveAssitance = hasItems;

    return isTryingToRemoveAssitance ? onRemoveAllItems() : onAddItem();
  };

  const selectPassenger = intl.formatMessage({ id: 'addAssistance.selectPassenger' });
  const selectAnimal = intl.formatMessage({ id: 'addAssistance.selectAnimal' });

  const isAddPassengerVisible = Boolean(isItemsVisible && !isAddPassengersDisabled);

  const maxCommentLength = 200;
  const commentWarningMessage = `(Max length ${maxCommentLength})`;
  const numberWarningMessage = `(Please type a valid number)`;

  const calcPrice = () => {
    const total = items.reduce(
      (acc, item) =>
        acc +
        ([BOTH, OUTBOUND].includes(assistance.direction) ? assistance.price * item.numberOfOutboundItems : 0) +
        ([BOTH, INBOUND].includes(assistance.direction) ? assistance.price * item.numberOfInboundItems : 0),
      0
    );

    return total > 0 ? ` - ${currencies[currency]}${total}` : '';
  };

  const isDogAssistance = assistance.id === ASSISTANCE_KEYS.GUIDE_DOG_ASSIST;
  const isDogAssistanceAndHasDogPassenger = hasDogPassenger && isDogAssistance;

  return (
    <StyledAssistanceGroup data-cy={assistance.id} data-testid={assistance.id}>
      <StyledAssistanceGroupHeader data-cy="toggle">
        <Checkbox
          disabled={isDogAssistanceAndHasDogPassenger}
          checked={hasItems}
          onChange={handleAssistanceSelection}
          dataCy={`${assistance.id}-checkbox`}
        />
        <StyledAssistanceToggle
          type="button"
          onClick={onToggleItemsVisibility}
          isActive={isItemsVisible}
          data-testid={`${assistance.id}-toggle`}
        >
          {assistance.name}
          {isExtra && assistance.availableUnits !== undefined && (
            <>
              {' '}
              (<FormattedMessage id="addExtras.availableExtras" values={{ amount: assistance.availableUnits }} />)
            </>
          )}
          {isExtra && calcPrice()}
          <StyledChevron />
        </StyledAssistanceToggle>

        {isIncomplete && (
          <StyledWarningMessage isAlerted={!isItemsVisible} data-testid="warning-message">
            * <FormattedMessage id="addAssistance.incompleteWarning" />
          </StyledWarningMessage>
        )}
      </StyledAssistanceGroupHeader>

      {items.map((item) => {
        const uniqueId = (name) => `${assistance.id}-${item.id}-${name}`;
        const isWarningVisible = item.comment.length > 195;
        const isNumberWarningVisible =
          Number.isNaN(item.numberOfOutboundItems) ||
          Number.isNaN(item.numberOfInboundItems) ||
          (Number(item.numberOfOutboundItems) < 1 && Number(item.numberOfInboundItems) < 1);

        return (
          <StyledItemsContainer data-cy="item" key={`${assistance.id}-${item.id}`} data-testid={`${assistance.id}-${item.id}`}>
            <StyledItems visible={isItemsVisible} data-testid={`styledItems-${assistance.id}-${item.id}`}>
              <StyledRemoveButton type="button" onClick={() => onRemoveItem(item)} data-testid={`${assistance.id}-${item.id}-remove`}>
                <FormattedMessage id="addAssistance.remove" />
              </StyledRemoveButton>

              <StyledItem>
                <StyledItemColumns>
                  <StyledItemColumn>
                    <StyledStrong>
                      <FormattedMessage id="addAssistance.passenger" />
                      {!isExtra && ' *'}
                    </StyledStrong>

                    <Select
                      defaultValue={paxIdHeterogenous ? paxIdHeterogenous : item.passengerId || ''}
                      name={uniqueId('passengerId')}
                      data-testid={uniqueId('passengerId')}
                      onChange={(event) => onChangeItem({ ...item, passengerId: event.target.value })}
                    >
                      <option disabled value="">
                        {selectPassenger}
                      </option>

                      {passengers.map((passenger) => (
                        <option key={passenger.id} value={passenger.id} disabled={isOptionDisabled(passenger.id)}>
                          {getPassengerLabel(intl, passenger, isGroupBooking)}
                        </option>
                      ))}
                    </Select>
                  </StyledItemColumn>
                  {!isExtra && !isDogAssistance && (
                    <StyledItemColumn>
                      <StyledStrong>
                        <FormattedMessage id="addAssistance.passengerDisability" /> *
                      </StyledStrong>

                      <Select
                        defaultValue={item.passengerDisability || PASSENGER_DISABILITY_KEYS.NONE}
                        name={uniqueId('passengerDisability')}
                        data-testid={uniqueId('passengerDisability')}
                        onChange={(event) => onChangeItem({ ...item, passengerDisability: event.target.value })}
                      >
                        {PASSENGER_DISABILITY_OPTIONS.map(({ id, translationId }) => (
                          <option key={id} value={id}>
                            {intl.formatMessage({ id: translationId })}
                          </option>
                        ))}
                      </Select>
                    </StyledItemColumn>
                  )}

                  {isDogAssistance && (
                    <StyledItemColumn>
                      <StyledStrong>
                        <FormattedMessage id="addAssistance.assistanceAnimal" /> *
                      </StyledStrong>

                      <Select
                        defaultValue={item.assistanceAnimal || ''}
                        name={uniqueId('assistanceAnimal')}
                        data-testid={uniqueId('assistanceAnimal')}
                        onChange={(event) => onChangeItem({ ...item, assistanceAnimal: event.target.value })}
                      >
                        <option disabled value="">
                          {selectAnimal}
                        </option>
                        {ASSISTANCE_ANIMAL_OPTIONS.map(({ id, translationId }) => (
                          <option key={id} value={id}>
                            {intl.formatMessage({ id: translationId })}
                          </option>
                        ))}
                      </Select>
                    </StyledItemColumn>
                  )}

                  {!isExtra && (
                    <StyledItemColumn>
                      <StyledStrong>
                        <FormattedMessage id="addAssistance.journey" /> *
                      </StyledStrong>

                      <StyledRadioGroup>
                        {directions.map((d) => {
                          const uniqOptionId = `${uniqueId('direction')}-${d.id}`;
                          const isOneWayJourney = directions.length === 1;

                          return (
                            <div key={uniqOptionId}>
                              {isOneWayJourney ? (
                                <FormattedMessage id={d.translationId} />
                              ) : (
                                <label htmlFor={uniqOptionId}>
                                  <StyledRadio
                                    type="radio"
                                    id={uniqOptionId}
                                    data-testid={uniqOptionId}
                                    name={uniqueId('direction')}
                                    value={d.id}
                                    defaultChecked={d.id === getAssistanceDirection(item)}
                                    onChange={(event) =>
                                      onChangeItem({
                                        ...item,
                                        numberOfOutboundItems: [BOTH, OUTBOUND].includes(event.target.value) ? 1 : 0,
                                        numberOfInboundItems: [BOTH, INBOUND].includes(event.target.value) ? 1 : 0
                                      })
                                    }
                                  />
                                  <FormattedMessage id={d.translationId} />
                                </label>
                              )}
                            </div>
                          );
                        })}
                      </StyledRadioGroup>
                    </StyledItemColumn>
                  )}

                  {isExtra && (
                    <StyledItemColumn>
                      <StyledStrong>
                        <FormattedMessage id="extrasOnlyBookings.fields.quantity" />
                        {isNumberWarningVisible && <StyledCommentWarning>{numberWarningMessage}</StyledCommentWarning>}
                      </StyledStrong>
                      <StyledNumberInput
                        type="number"
                        min="0"
                        id={uniqueId('quantity')}
                        name={uniqueId('quantity')}
                        data-testid={uniqueId('quantity')}
                        value={assistance.direction === OUTBOUND ? item.numberOfOutboundItems : item.numberOfInboundItems}
                        onChange={(event) => {
                          const quantity = Number(event.target.value);
                          onChangeItem({
                            ...item,
                            numberOfOutboundItems: assistance.direction === OUTBOUND ? quantity : 0,
                            numberOfInboundItems: assistance.direction === INBOUND ? quantity : 0
                          });
                        }}
                      />
                    </StyledItemColumn>
                  )}
                  {!isExtra && (
                    <StyledItemColumn>
                      <StyledStrong>
                        <FormattedMessage id="addAssistance.comment" />
                        {isWarningVisible && <StyledCommentWarning>{commentWarningMessage}</StyledCommentWarning>}
                      </StyledStrong>

                      <StyledTextarea
                        value={item.comment}
                        maxLength={maxCommentLength}
                        id={uniqueId('comment')}
                        name={uniqueId('comment')}
                        data-testid={uniqueId('comment')}
                        onChange={(event) => onChangeItem({ ...item, comment: event.target.value })}
                      />
                    </StyledItemColumn>
                  )}
                </StyledItemColumns>
              </StyledItem>
            </StyledItems>
          </StyledItemsContainer>
        );
      })}

      {isAddPassengerVisible && (
        <StyledButtonWrapper>
          <StyledAddAnotherPassenger
            type="button"
            onClick={onAddItem}
            disabled={isAddPassengersDisabled}
            data-cy="addPassenger"
            data-testid="addPassenger"
          >
            + <FormattedMessage id="addAssistance.addPassenger" />
          </StyledAddAnotherPassenger>
        </StyledButtonWrapper>
      )}
    </StyledAssistanceGroup>
  );
};

AddAssistanceOptions.propTypes = {
  intl: intlShape.isRequired,
  currency: PropTypes.string,
  isIncomplete: PropTypes.bool.isRequired,
  assistance: PropTypes.shape({
    id: PropTypes.string.isRequired,
    direction: PropTypes.string,
    name: PropTypes.string.isRequired,
    passengerTypes: PropTypes.arrayOf(PropTypes.string),
    passengers: PropTypes.arrayOf(PropTypes.string),
    price: PropTypes.number,
    availableUnits: PropTypes.number
  }).isRequired,
  isExtra: PropTypes.bool.isRequired,
  isGroupBooking: PropTypes.bool,
  directions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      translationId: PropTypes.string.isRequired
    })
  ).isRequired,
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired
    })
  ).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      passengerId: PropTypes.string.isRequired,
      numberOfOutboundItems: PropTypes.number,
      numberOfInboundItems: PropTypes.number,
      comment: PropTypes.string.isRequired,
      passengerDisability: PropTypes.string
    })
  ).isRequired,
  isAddPassengersDisabled: PropTypes.bool.isRequired,
  isItemsVisible: PropTypes.bool.isRequired,
  onAddItem: PropTypes.func.isRequired,
  onChangeItem: PropTypes.func.isRequired,
  onRemoveAllItems: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onToggleItemsVisibility: PropTypes.func.isRequired
};

export default injectIntl(AddAssistanceOptions);
